import React, { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PasskeyProvider, usePasskey } from './contexts/PasskeyContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './components/Login';
import Home from './pages/Home';
import ThoughtEditor from './pages/ThoughtEditor';
import PasskeyPrompt from './pages/PasskeyPrompt';
import PasskeySettings from './pages/PasskeySettings';
import Diary from './pages/Diary';
import Journals from './pages/Journals';
import JournalEditor from './pages/JournalEditor';
import Download from './pages/Download';

interface RouteWrapperProps {
  children: ReactNode;
}

const PrivateRoute: React.FC<RouteWrapperProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const { passkey, checkingPasskey } = usePasskey();
  
  if (loading || checkingPasskey) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (!passkey) {
    return <PasskeyPrompt />;
  }
  
  return <>{children}</>;
};

const PublicRoute: React.FC<RouteWrapperProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return null;
  }
  
  return !currentUser ? <>{children}</> : <Navigate to="/" />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Home />
          </PrivateRoute>
        }
      />
      <Route
        path="/thought/:id"
        element={
          <PrivateRoute>
            <ThoughtEditor />
          </PrivateRoute>
        }
      />
      <Route
        path="/diary"
        element={
          <PrivateRoute>
            <Diary />
          </PrivateRoute>
        }
      />
      <Route
        path="/journals"
        element={
          <PrivateRoute>
            <Journals />
          </PrivateRoute>
        }
      />
      <Route
        path="/journal/:id"
        element={
          <PrivateRoute>
            <JournalEditor />
          </PrivateRoute>
        }
      />
      <Route
        path="/download"
        element={
          <PrivateRoute>
            <Download />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/passkey"
        element={
          <PrivateRoute>
            <PasskeySettings />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <CssBaseline />
      <AuthProvider>
        <PasskeyProvider>
          <Router>
            <AppRoutes />
          </Router>
        </PasskeyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
